import { useRef } from "react";
import { Billboard } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import type * as THREE from "three";

import { useSpeakingLevel } from "./useSpeakingLevel";
import { useVoiceStore } from "./store";

interface FaceBillboardProps {
  /** LiveKit identity / Colyseus sessionId of the player this face belongs to. */
  identity: string;
  /** Y-offset above the avatar origin. Tune per character height. */
  y?: number;
  /** Scale of the whole face tile. */
  scale?: number;
}

/**
 * Floating, camera-facing face tile rendered above each remote player.
 * Simple geometric face — round head, two eye dots, a wide rectangular
 * mouth whose vertical scale tracks the speaker's VU level.
 *
 * Adds a faint outer ring that brightens while the player is in the
 * LiveKit ActiveSpeakers set, so listeners can find the source even when
 * looking away from a chatty player.
 */
export function FaceBillboard({
  identity,
  y = 1.8,
  scale = 0.42,
}: FaceBillboardProps): JSX.Element {
  const levelRef = useSpeakingLevel(identity);
  const mouthRef = useRef<THREE.Mesh>(null);
  const glowRef = useRef<THREE.Mesh>(null);
  const glowMatRef = useRef<THREE.MeshBasicMaterial>(null);

  useFrame(() => {
    const m = mouthRef.current;
    if (m) {
      // Mouth is a thin rectangle; opening = scale it vertically. Idle
      // resting height = 0.05, fully open = 0.55.
      const open = 0.05 + levelRef.current * 0.5;
      m.scale.set(1, open / 0.05, 1);
    }
    const g = glowRef.current;
    const gm = glowMatRef.current;
    if (g && gm) {
      // ActiveSpeakers transitions on a coarser threshold than the VU
      // meter; combine both for a snappy glow that still reflects volume.
      const isActive = useVoiceStore.getState().activeSpeakers.has(identity);
      const target = isActive ? 0.8 : Math.min(0.5, levelRef.current * 1.2);
      const cur = gm.opacity;
      gm.opacity = cur + (target - cur) * 0.2;
      const s = 1.05 + (gm.opacity - 0.1) * 0.18;
      g.scale.set(s, s, 1);
    }
  });

  return (
    <Billboard position={[0, y, 0]} follow lockX={false} lockY={false} lockZ={false}>
      <group scale={[scale, scale, scale]}>
        {/* Glow ring — slightly behind the face, additive-ish via opacity. */}
        <mesh ref={glowRef} position={[0, 0, -0.01]}>
          <circleGeometry args={[0.62, 32]} />
          <meshBasicMaterial
            ref={glowMatRef}
            color="#7df0a8"
            transparent
            opacity={0}
            depthWrite={false}
          />
        </mesh>

        {/* Head disc. */}
        <mesh>
          <circleGeometry args={[0.55, 32]} />
          <meshBasicMaterial color="#f0d8a8" />
        </mesh>

        {/* Eyes — two small dots. */}
        <mesh position={[-0.18, 0.12, 0.001]}>
          <circleGeometry args={[0.06, 16]} />
          <meshBasicMaterial color="#1a1208" />
        </mesh>
        <mesh position={[0.18, 0.12, 0.001]}>
          <circleGeometry args={[0.06, 16]} />
          <meshBasicMaterial color="#1a1208" />
        </mesh>

        {/* Mouth — flat rectangle, vertically scaled by VU level. The
            non-uniform .scale.y on the mesh achieves the open/close. */}
        <mesh ref={mouthRef} position={[0, -0.18, 0.001]}>
          <planeGeometry args={[0.28, 0.05]} />
          <meshBasicMaterial color="#2a0e0e" />
        </mesh>
      </group>
    </Billboard>
  );
}
